import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Tabs,
    Tab,
    Avatar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Menu,
    Chip,
    InputAdornment,
    Paper,
    GlobalStyles,
    Autocomplete,
    Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PrintDisabledIcon from "@mui/icons-material/PrintDisabled";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { z } from "zod";
import {
    Calendar,
    Clock,
    User,
    BookOpen,
    Layers,
    Video,
    MapPin,
    FileText,
    Settings,
    ChevronDown,
    ChevronUp,
    X,
    MoreHorizontal,
    PlusCircle,
    Users,
    Eye
} from "lucide-react";
import { motion } from "framer-motion";
import UploadButtonField from "../../../../Components/Button/UploadButtonField";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useAppSelector, useAppDispatch } from "../../../../Redux/Store/Hooks";
import { createMeetLink, CreateMeetLinkParams, resetCreateMeetLink } from "../../../../Redux/Slices/ScheduledClasses/CreateMeetLinkSlice";
import { createScheduledClass, resetCreateScheduledClass } from "../../../../Redux/Slices/ScheduledClasses/CreateScheduledClassSlice";
import GoogleMeetIcon from "../../../../Assets/Images/meet-icon.png";

interface MeetingModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    onBookletTag?: (booklets: string[]) => void;
    onRefboardUpload?: (file: File) => void;
    selectedDate: Dayjs | null;
    initialData?: any;
}

const scheduleSchema = z.object({
    title: z.string().min(1, "Class title is required"),
    tutor: z.string().min(1, "Please assign a tutor"),
    subject: z.string().min(1, "Please select a subject"),
    topic: z.string().optional(),
    members: z.array(z.union([z.string(), z.number()])).min(1, "Please add at least one member/student"),
    meetingLink: z.string().min(1, "Please add a meeting link"),
});

const MeetingModal: React.FC<MeetingModalProps> = ({
    open,
    onClose,
    onSave,
    onBookletTag,
    onRefboardUpload,
    selectedDate,
    initialData,
}) => {
    const [title, setTitle] = useState("");
    const [startTime, setStartTime] = useState<Dayjs | null>(dayjs().hour(12).minute(30));
    const [endTime, setEndTime] = useState<Dayjs | null>(dayjs().hour(13).minute(30));
    const [localDate, setLocalDate] = useState<Dayjs | null>(null);
    const [meetingLink, setMeetingLink] = useState("");
    const [description, setDescription] = useState("");
    const [booklets, setBooklets] = useState<string[]>([]);
    const [timezone, setTimezone] = useState("UTC");
    const [refboardFiles, setRefboardFiles] = useState<File[]>([]);
    const [worksheetFiles, setWorksheetFiles] = useState<File[]>([]);
    const [existingWorksheets, setExistingWorksheets] = useState<any[]>([]);
    const [existingSampleBooklets, setExistingSampleBooklets] = useState<any[]>([]);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isJoinConfirmOpen, setIsJoinConfirmOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");

    const { data: setupData } = useAppSelector((state) => state.ScheduleSetupDataSlice);
    const apiTutors = setupData?.data?.tutors || [];
    const apiStudents = setupData?.data?.students || [];
    const apiQuestionTypes = setupData?.data?.question_types || [];

    // Deduplicate options by unique ID to prevent real data duplicates, 
    // while allowing different students with the same name to exist.
    const deduplicatedOptions = React.useMemo(() => {
        if (!Array.isArray(apiStudents) || apiStudents.length === 0) return [];

        const studentMap = new Map();
        apiStudents.forEach(s => {
            if (s?.id) {
                if (!studentMap.has(s.id)) {
                    studentMap.set(s.id, s);
                }
            }
        });
        return Array.from(studentMap.values());
    }, [apiStudents]);

    const deduplicatedTutors = React.useMemo(() => {
        if (!Array.isArray(apiTutors) || apiTutors.length === 0) return [];
        const tutorMap = new Map();
        apiTutors.forEach(t => {
            if (t?.id) {
                if (!tutorMap.has(t.id)) {
                    tutorMap.set(t.id, t);
                }
            }
        });
        return Array.from(tutorMap.values());
    }, [apiTutors]);

    const deduplicatedQuestionTypes = React.useMemo(() => {
        if (!Array.isArray(apiQuestionTypes) || apiQuestionTypes.length === 0) return [];
        const qtMap = new Map();
        apiQuestionTypes.forEach(q => {
            if (q?.id) {
                const key = (q.name || "").trim();
                if (!qtMap.has(key)) {
                    qtMap.set(key, q);
                }
            }
        });
        return Array.from(qtMap.values());
    }, [apiQuestionTypes]);


    const [tutor, setTutor] = useState("");
    const [subject, setSubject] = useState("");
    const [topic, setTopic] = useState("");
    const [subjectId, setSubjectId] = useState<number | string>("");
    const [questionTypeId, setQuestionTypeId] = useState<number | string>("");
    const [members, setMembers] = useState<(string | number)[]>([]); // Using IDs for internal state, allowing string compatibility during transitions
    const [permissions, setPermissions] = useState({
        shareScreen: true,
        recording: true,
        voice: true,
        chat: true,
        fileShare: true
    });
    const dispatch = useAppDispatch();
    const { data: meetLinkData, isLoading: isMeetLoading, error: meetError } = useAppSelector((state) => state.CreateMeetLinkSlice);
    // Read-only for existing classes whose start time is NOT strictly in the future
    const isReadOnly = !!(initialData?.id && initialData?.startTime && !dayjs(initialData.startTime).isAfter(dayjs()));
    const { data: createData, isLoading: isCreateLoading, error: createError } = useAppSelector((state) => state.CreateScheduledClassSlice);

    const calculateDuration = () => {
        if (startTime && endTime) {
            const diffInMinutes = endTime.diff(startTime, 'minute');
            if (diffInMinutes <= 0) return "";
            const hours = Math.floor(diffInMinutes / 60);
            const minutes = diffInMinutes % 60;
            let str = "";
            if (hours > 0) str += `${hours} hr${hours > 1 ? 's' : ''} `;
            if (minutes > 0) str += `${minutes} min${minutes > 1 ? 's' : ''}`;
            return str.trim();
        }
        return "";
    };

    useEffect(() => {
        if (createData?.status && open) {
            dispatch(resetCreateScheduledClass());
            onClose();
        }
    }, [createData, dispatch, open, onClose]);

    useEffect(() => {
        if (createError && open) {
            console.error("Error creating scheduled class:", createError);
            // You can add a toast notification or alert here
            dispatch(resetCreateScheduledClass());
        }
    }, [createError, dispatch, open]);

    useEffect(() => {
        if (meetLinkData?.status && meetLinkData?.data?.meet_link && open) {
            setMeetingLink(meetLinkData.data.meet_link);
            dispatch(resetCreateMeetLink());
        }
    }, [meetLinkData, dispatch, open]);

    useEffect(() => {
        if (meetError && open) {
            console.error("Error generating meet link:", meetError);
            // Optionally set an alert or error state here
            dispatch(resetCreateMeetLink());
        }
    }, [meetError, dispatch, open]);

    useEffect(() => {
        if (open) {
            // Always clear pending local files and errors when modal opens
            setWorksheetFiles([]);
            setRefboardFiles([]);
            setFormErrors({});

            if (initialData && initialData.id) {
                setTitle(initialData.title || "");
                setMeetingLink(initialData.meetingLink || "");
                setDescription(initialData.description || "");
                setTutor(initialData.tutor || "");
                setSubject(initialData.subjectName || initialData.subject || "");
                setTopic(initialData.questionTypeName || initialData.topic || "");
                setSubjectId(initialData.subjectId || "");
                setQuestionTypeId(initialData.questionTypeId || "");
                setBooklets(Array.isArray(initialData.booklets) ? initialData.booklets : (initialData.booklet ? [initialData.booklet] : []));
                setMembers(initialData.members || []);
                setExistingWorksheets(initialData.worksheets || []);
                setExistingSampleBooklets(initialData.sampleBooklets || []);
                setLocalDate(dayjs(initialData.date || initialData.startTime));
                setStartTime(dayjs(initialData.startTime));
                setEndTime(dayjs(initialData.endTime));
                setPermissions(initialData.permissions || {
                    shareScreen: true,
                    recording: true,
                    voice: true,
                    chat: true,
                    fileShare: true
                });
            } else if (selectedDate) {
                setTitle("");
                setMeetingLink("");
                setDescription("");
                setTutor("");
                setSubject(initialData?.subject || "");
                setTopic(initialData?.topic || "");
                setSubjectId(initialData?.subjectId || "");
                setQuestionTypeId(initialData?.questionTypeId || "");
                setBooklets([]);
                setMembers([]);
                setExistingWorksheets([]);
                setExistingSampleBooklets([]);
                setPermissions({
                    shareScreen: true,
                    recording: true,
                    voice: true,
                    chat: true,
                    fileShare: true
                });
                setLocalDate(dayjs(selectedDate));
                setStartTime(dayjs(selectedDate).minute(0));
                setEndTime(dayjs(selectedDate).add(1, "hour").minute(0));
            }

            // Timezone Detection (Resolved to full IANA name as requested)
            if (initialData && initialData.timezone_str) {
                setTimezone(initialData.timezone_str);
            } else {
                const ianaTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                setTimezone(ianaTz || "UTC");
            }
        }
    }, [open, selectedDate, initialData]);


    const handleGenerateLink = () => {
        const duration = (endTime && startTime) ? endTime.diff(startTime, 'minute') : 60;

        const params: CreateMeetLinkParams = {
            meeting_title: title || "(No title)",
            duration_minutes: duration,
            start_date: localDate?.format("YYYY-MM-DD") || selectedDate?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
            start_time: startTime?.format("HH:mm") || dayjs().format("HH:mm"),
            end_date: localDate?.format("YYYY-MM-DD") || selectedDate?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
            end_time: endTime?.format("HH:mm") || dayjs().add(1, 'hour').format("HH:mm"),
            timezone_str: timezone,
            is_recurring: false
        };

        dispatch(createMeetLink(params));
    };

    const handleSave = () => {
        // Form Validation with Zod
        const validationResult = scheduleSchema.safeParse({
            title,
            tutor,
            subject,
            topic,
            members,
            meetingLink
        });

        if (!validationResult.success) {
            const errors: Record<string, string> = {};
            validationResult.error.errors.forEach((err) => {
                if (err.path[0]) {
                    errors[err.path[0] as string] = err.message;
                }
            });
            setFormErrors(errors);
            return;
        }

        setFormErrors({});

        // Collect student IDs - The state now handles IDs directly
        const selectedStudentIds = Array.from(new Set(
            members.map(member => {
                if (typeof member === "number") return member;
                const student = apiStudents.find(s => (s.username || "").trim() === member.trim());
                return student?.id;
            }).filter((id): id is number => id !== undefined)
        ));

        // Find tutor ID
        const selectedTutor = apiTutors.find(t => t.username === tutor);
        const tutorId = selectedTutor ? selectedTutor.id : 0;

        // Find subject and question type IDs if applicable
        const selectedQuestionType = apiQuestionTypes.find(q => q.name === topic);
        const finalQuestionTypeId = questionTypeId || (selectedQuestionType ? selectedQuestionType.id : undefined);
        const finalSubjectId = subjectId || (selectedQuestionType ? selectedQuestionType.subject_id : undefined);

        // Construct from_time and to_time in ISO format
        const finalLocalDate = localDate || selectedDate || dayjs();
        const fromDateObj = finalLocalDate.hour(startTime?.hour() || 0).minute(startTime?.minute() || 0).second(0);
        const toDateObj = finalLocalDate.hour(endTime?.hour() || 0).minute(endTime?.minute() || 0).second(0);
        const fromTime = fromDateObj.format("YYYY-MM-DDTHH:mm:ss");
        const toTime = toDateObj.format("YYYY-MM-DDTHH:mm:ss");

        // Future Time Validation
        if (fromDateObj.isBefore(dayjs())) {
            setFormErrors(prev => ({ ...prev, time: "Please select a future date and time." }));
            return;
        }
        if (toDateObj.isBefore(fromDateObj)) {
            setFormErrors(prev => ({ ...prev, time: "End time must be after start time." }));
            return;
        }

        // Separate files from IDs for sample booklets and worksheets
        const sampleBookletFiles = refboardFiles.filter(file => file instanceof File);
        const worksheetFilesToUpload = worksheetFiles.filter(file => file instanceof File);

        const class_data = {
            id: initialData?.id, // include if updating
            class_title: title || "(No title)",
            from_time: fromTime,
            to_time: toTime,
            tutor_id: tutorId,
            participants: selectedStudentIds,
            meeting_link: meetingLink,
            description: description,
            question_type_id: finalQuestionTypeId,
            subject_id: finalSubjectId,
            is_recurring: false,
            timezone_str: timezone,
            sample_booklets: existingSampleBooklets.map(b => b.id), // Only IDs in class_data
            worksheets: existingWorksheets.map(w => w.id) // Only IDs in class_data
        };

        const params = {
            class_data,
            worksheets: worksheetFilesToUpload.length > 0 ? worksheetFilesToUpload : undefined,
            sample_booklet_files: sampleBookletFiles.length > 0 ? sampleBookletFiles : undefined,
        };

        dispatch(createScheduledClass(params as any));

        if (onRefboardUpload && refboardFiles.length > 0) {
            onRefboardUpload(refboardFiles[0]); // Keep existing single callback compatibility if needed
        }

        onSave({
            ...class_data,
            startTime: dayjs(fromTime),
            endTime: dayjs(toTime),
            date: finalLocalDate.format("YYYY-MM-DD"),
            tutor,
            members,
            topic,
            subject,
            booklets,
            permissions,
            worksheetFiles,
            refboardFiles
        });
    };

    // const handlePreviewBooklet = (e: React.MouseEvent, title: string) => {
    //     e.stopPropagation();
    //     const booklet = tutoringData.booklets.find(b => b.title === title);
    //     if (booklet && booklet.drive_link) {
    //         setPreviewUrl(booklet.drive_link);
    //         setPreviewTitle(booklet.title);
    //         setIsPreviewOpen(true);
    //     }
    // };

    const handlePreviewWorksheet = (e: React.MouseEvent, ws: any) => {
        e.stopPropagation();
        if (ws.drive_file_url) {
            setPreviewUrl(ws.drive_file_url);
            setPreviewTitle(ws.drive_file_name);
            setIsPreviewOpen(true);
        }
    };

    useEffect(() => {
        const handlePrintPrevention = (e: KeyboardEvent) => {
            if (isPreviewOpen && (e.key === 'PrintScreen' || (e.ctrlKey && e.key === 'p'))) {
                e.preventDefault();
                alert('Printing is disabled for this document.');
            }
        };

        if (isPreviewOpen) {
            window.addEventListener('keydown', handlePrintPrevention);
        }
        return () => window.removeEventListener('keydown', handlePrintPrevention);
    }, [isPreviewOpen]);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: "28px",
                        p: 0,
                        overflow: "hidden",
                        boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
                    }
                }}
            >
                {/* Header / Top Bar */}
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                    bgcolor: "#fcfcfc"
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "10px",
                            bgcolor: "#e3f2fd",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <Calendar size={18} color="#1a73e8" />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1a1c1e" }}>
                            {isReadOnly ? "Class Details" : (initialData?.id ? "Edit Class" : "Schedule New Class")}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: "#5f6368" }}>
                        <X size={20} />
                    </IconButton>
                </Box>

                <DialogContent
                    sx={{
                        px: 4,
                        py: 3,
                        "&::-webkit-scrollbar": { display: "none" },
                        overflowX: "hidden"
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                        <Box sx={{ mb: 4 }}>
                            <TextField
                                fullWidth
                                placeholder="Class Title *"
                                variant="standard"
                                value={title}
                                disabled={isReadOnly}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    if (formErrors.title) setFormErrors(prev => ({ ...prev, title: "" }));
                                }}
                                autoFocus={!isReadOnly}
                                error={!!formErrors.title}
                                helperText={formErrors.title}
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        fontSize: "22px",
                                        fontWeight: 400,
                                        color: "#1a1c1e",
                                        "& input::placeholder": { color: "#bdc1c6", opacity: 1 }
                                    }
                                }}
                            />
                            <Box sx={{ height: "2px", width: "100%", bgcolor: formErrors.title ? "#d32f2f" : "#1a73e8", mt: 0.5, opacity: 0.6, borderRadius: "1px" }} />
                        </Box>


                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
                                {/* Time & Date Selection */}
                                <Box sx={{ display: "flex", gap: 2.5 }}>
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "10px",
                                        bgcolor: "#fff4e5",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}>
                                        <Clock size={20} color="#ed6c02" />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#3c4043", mb: 0.5 }}>
                                            Date & Time <span style={{ color: '#d32f2f' }}>*</span>
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" , justifyContent:'space-between'}}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        value={localDate}
                                                        disabled={isReadOnly}
                                                        onChange={(v) => {
                                                            setLocalDate(v);
                                                            if (formErrors.time) setFormErrors(prev => ({ ...prev, time: "" }));
                                                        }}
                                                        format="DD MMM YYYY"
                                                        slotProps={{
                                                            textField: {
                                                                variant: "outlined",
                                                                size: "small",
                                                                sx: {
                                                                    width: 190,
                                                                    "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#f1f3f4", border: "none", "& fieldset": { border: "none" } }
                                                                }
                                                            },
                                                            popper: {
                                                                sx: {
                                                                    "& .MuiPickersLayout-root": { borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" },
                                                                    "& .MuiPickersDay-root.Mui-selected": { bgcolor: "#1a73e8 !important", color: "#fff !important" },
                                                                    "& .MuiPickersDay-root:hover": { bgcolor: "rgba(26, 115, 232, 0.1) !important" }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <TimePicker
                                                            value={startTime}
                                                            disabled={isReadOnly}
                                                            onChange={(v) => {
                                                                setStartTime(v);
                                                                if (formErrors.time) setFormErrors(prev => ({ ...prev, time: "" }));
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: "outlined",
                                                                    size: "small",
                                                                    sx: {
                                                                        width: 120,
                                                                        "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#f1f3f4", border: "none", "& fieldset": { border: "none" } }
                                                                    }
                                                                },
                                                                popper: {
                                                                    sx: {
                                                                        "& .MuiPickersLayout-root": { borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" },
                                                                        "& .MuiMultiSectionDigitalClock-root": { bgcolor: "#fff !important" },
                                                                        "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": { 
                                                                            bgcolor: "#1a73e8 !important", 
                                                                            color: "#fff !important",
                                                                            background: "#1a73e8 !important" 
                                                                        },
                                                                        "& .MuiMultiSectionDigitalClockSection-item:hover": { bgcolor: "rgba(26, 115, 232, 0.1) !important" }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <Typography variant="body2" color="textSecondary">–</Typography>
                                                        <TimePicker
                                                            value={endTime}
                                                            disabled={isReadOnly}
                                                            onChange={(v) => {
                                                                setEndTime(v);
                                                                if (formErrors.time) setFormErrors(prev => ({ ...prev, time: "" }));
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: "outlined",
                                                                    size: "small",
                                                                    sx: {
                                                                        width: 120,
                                                                        "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#f1f3f4", border: "none", "& fieldset": { border: "none" } }
                                                                    }
                                                                },
                                                                popper: {
                                                                    sx: {
                                                                        "& .MuiPickersLayout-root": { borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)" },
                                                                        "& .MuiMultiSectionDigitalClock-root": { bgcolor: "#fff !important" },
                                                                        "& .MuiMultiSectionDigitalClockSection-item.Mui-selected": { 
                                                                            bgcolor: "#1a73e8 !important", 
                                                                            color: "#fff !important",
                                                                            background: "#1a73e8 !important" 
                                                                        },
                                                                        "& .MuiMultiSectionDigitalClockSection-item:hover": { bgcolor: "rgba(26, 115, 232, 0.1) !important" }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                </LocalizationProvider>
                                            </Box>
                                            {calculateDuration() && (
                                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <Typography variant="caption" sx={{
                                                        color: "#1a73e8",
                                                        fontWeight: 600,
                                                        bgcolor: "#e8f0fe",
                                                        px: 1.8,
                                                        py: 0.6,
                                                        fontSize:"12.5px",
                                                        borderRadius: "20px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1
                                                    }}>
                                                      <Clock size={14} /> Duration: {calculateDuration()}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        {formErrors.time && <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>{formErrors.time}</Typography>}
                                    </Box>
                                </Box>

                                {/* Class Details Section */}
                                <Box sx={{ display: "flex", gap: 2.5 }}>
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "10px",
                                        bgcolor: "#f3e5f5",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}>
                                        <Layers size={20} color="#9c27b0" />
                                    </Box>
                                    <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                        <FormControl fullWidth size="small" error={!!formErrors.tutor}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: formErrors.tutor ? "#d32f2f" : "#5f6368", mb: 0.5, ml: 0.5 }}>Assign to Tutor <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                            <Autocomplete
                                                id="tutor-autocomplete"
                                                options={deduplicatedTutors}
                                                disabled={isReadOnly}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === 'string') return option;
                                                    return option.email ? `${option.username} (${option.email})` : (option.username || "");
                                                }}
                                                renderOption={(props, option) => (
                                                    <Box component="li" {...props} sx={{ py: 1, display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 , fontSize:"12px"}}>
                                                            {option.username}
                                                            {option.email && (
                                                                <span style={{ color: "#5f6368", marginLeft: '6px', fontSize: '0.85em' }}>
                                                                    ({option.email})
                                                                </span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                value={deduplicatedTutors.find(t => t.username === tutor) || null}
                                                onChange={(_, newValue) => {
                                                    setTutor(newValue ? newValue.username : "");
                                                    if (formErrors.tutor) setFormErrors(prev => ({ ...prev, tutor: "" }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select Tutor"
                                                        error={!!formErrors.tutor}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "12px",
                                                                bgcolor: "#f8f9fa",
                                                                "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                                                                "&:hover fieldset": { borderColor: "#1a73e8" },
                                                                "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                            {formErrors.tutor && <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>{formErrors.tutor}</Typography>}
                                        </FormControl>

                                        <FormControl fullWidth size="small" error={!!formErrors.subject}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: formErrors.subject ? "#d32f2f" : "#5f6368", mb: 0.5, ml: 0.5 }}>Subject <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                            <Select
                                                value={subject}
                                                disabled={isReadOnly || !!initialData?.subject}
                                                onChange={(e) => {
                                                    setSubject(e.target.value);
                                                    if (formErrors.subject) setFormErrors(prev => ({ ...prev, subject: "" }));
                                                }}
                                                displayEmpty
                                                sx={{
                                                    borderRadius: "12px",
                                                    bgcolor: initialData?.subject ? "#f1f3f4" : "#f8f9fa",
                                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: formErrors.subject ? "#d32f2f" : "rgba(0,0,0,0.08)" }
                                                }}
                                            >
                                                {subject ? (
                                                    <MenuItem value={subject}>{subject}</MenuItem>
                                                ) : (
                                                    <MenuItem value="" disabled>No subjects available</MenuItem>
                                                )}
                                            </Select>
                                            {formErrors.subject && <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>{formErrors.subject}</Typography>}
                                        </FormControl>

                                        <FormControl fullWidth size="small" sx={{ gridColumn: "span 2" }} error={!!formErrors.topic}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: formErrors.topic ? "#d32f2f" : "#5f6368", mb: 0.5, ml: 0.5 }}>Sub-type / Topic</Typography>
                                            <Autocomplete
                                                id="topic-autocomplete"
                                                options={deduplicatedQuestionTypes}
                                                disabled={isReadOnly}
                                                getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || "")}
                                                value={deduplicatedQuestionTypes.find(q => q.name === topic) || null}
                                                onChange={(_, newValue) => {
                                                    const selectedName = newValue ? newValue.name : "";
                                                    setTopic(selectedName);
                                                    if (newValue) {
                                                        setQuestionTypeId(newValue.id);
                                                        setSubjectId(newValue.subject_id);
                                                    }
                                                    if (formErrors.topic) setFormErrors(prev => ({ ...prev, topic: "" }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Select Sub-type"
                                                        error={!!formErrors.topic}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "12px",
                                                                bgcolor: "#f8f9fa",
                                                                "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                                                                "&:hover fieldset": { borderColor: "#1a73e8" },
                                                                "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                            {formErrors.topic && <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>{formErrors.topic}</Typography>}
                                        </FormControl>
                                        <FormControl fullWidth size="small" sx={{ gridColumn: "span 2" }} error={!!formErrors.members}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: formErrors.members ? "#d32f2f" : "#5f6368", mb: 0.5, ml: 0.5 }}>Add Members / Students <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                            <Autocomplete
                                                multiple
                                                id="members-autocomplete"
                                                disabled={isReadOnly}
                                                options={deduplicatedOptions}
                                                // Map existing member IDs/usernames back to their objects from the deduplicated list
                                                value={members.map(id => {
                                                    return deduplicatedOptions.find(s => s.id === id || s.username === id) || id;
                                                }).filter(Boolean)}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === "string") return option;
                                                    if (typeof option === "number") return String(option);
                                                    return option.email ? `${option.username} (${option.email})` : (option?.username || "");
                                                }}
                                                isOptionEqualToValue={(option, value) => {
                                                    const getIdentifier = (item: any) => {
                                                        if (typeof item === "string" || typeof item === "number") return item;
                                                        return item?.id;
                                                    };
                                                    return getIdentifier(option) === getIdentifier(value);
                                                }}
                                                onChange={(_, newValue) => {
                                                    // Ensure we store unique identifiers in state
                                                    const uniqueMembers = Array.from(new Set(
                                                        newValue.map(v => typeof v === "string" ? v : v.id)
                                                    ));
                                                    setMembers(uniqueMembers);
                                                    if (formErrors.members) setFormErrors(prev => ({ ...prev, members: "" }));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder={(!members || members.length === 0) ? "Select Members" : ""}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "12px",
                                                                bgcolor: "#f8f9fa",
                                                                minHeight: "45px",
                                                                "& fieldset": { borderColor: formErrors.members ? "#d32f2f" : "rgba(0,0,0,0.08)" },
                                                                "&:hover fieldset": { borderColor: formErrors.members ? "#d32f2f" : "#1a73e8" },
                                                                "&.Mui-focused fieldset": { borderColor: "#1a73e8" }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                renderTags={(tagValue, getTagProps) =>
                                                    tagValue.map((option, index) => {
                                                        const label = typeof option === "string"
                                                            ? option
                                                            : option.email
                                                                ? `${option.username} (${option.email})`
                                                                : (option?.username || "");

                                                        return (
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                key={index}
                                                                label={label}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: "#e8f0fe",
                                                                    color: "#1a73e8",
                                                                    fontWeight: 500,
                                                                    "& .MuiChip-deleteIcon": {
                                                                        color: "#1a73e8",
                                                                        "&:hover": { color: "#d32f2f" }
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })
                                                }
                                                ListboxProps={{
                                                    sx: { maxHeight: 300, borderRadius: "12px" }
                                                }}
                                            />
                                            {formErrors.members && <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, ml: 1 }}>{formErrors.members}</Typography>}
                                        </FormControl>

                                        {/* <FormControl fullWidth size="small" sx={{ gridColumn: "span 2" }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#5f6368", mb: 0.5, ml: 0.5 }}>Tag Booklet</Typography>
                                            <Select
                                                multiple
                                                value={booklets}
                                                onChange={(e) => setBooklets(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (selected.length === 0) {
                                                        return <Typography variant="body2" sx={{ color: "#bdc1c6" }}>Select Booklet</Typography>;
                                                    }
                                                    return (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => (
                                                                <Chip
                                                                    key={value}
                                                                    label={value}
                                                                    size="small"
                                                                    onDelete={(e) => {
                                                                        e.stopPropagation();
                                                                        setBooklets(booklets.filter((b) => b !== value));
                                                                    }}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    icon={
                                                                        <VisibilityIcon
                                                                            sx={{
                                                                                fontSize: "14px !important",
                                                                                cursor: "pointer",
                                                                                ml: 1,
                                                                                "&:hover": { color: "#7b1fa2" }
                                                                            }}
                                                                            onClick={(e) => handlePreviewBooklet(e, value)}
                                                                        />
                                                                    }
                                                                    sx={{
                                                                        borderRadius: "6px",
                                                                        bgcolor: "#f3e5f5",
                                                                        color: "#9c27b0",
                                                                        fontWeight: 500,
                                                                        height: "24px",
                                                                        "& .MuiChip-icon": { order: 1, ml: "4px", mr: "-4px" },
                                                                        "& .MuiChip-label": { pr: "8px" }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    );
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: { maxHeight: 300, borderRadius: "12px", mt: 1 }
                                                    },
                                                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                                    transformOrigin: { vertical: "top", horizontal: "left" }
                                                }}
                                                sx={{
                                                    borderRadius: "12px",
                                                    bgcolor: "#f8f9fa",
                                                    minHeight: "45px",
                                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.08)" },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1a73e8" }
                                                }}
                                            >
                                                {tutoringData.booklets.map((item: any) => (
                                                    <MenuItem key={item.id} value={item.title} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <Checkbox checked={booklets.indexOf(item.title) > -1} size="small" />
                                                            {item.title}
                                                        </Box>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handlePreviewBooklet(e, item.title)}
                                                            sx={{ color: "#9c27b0", "&:hover": { bgcolor: "#f3e5f5" } }}
                                                        >
                                                            <VisibilityIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl> */}

                                        <Box sx={{ gridColumn: "span 2", mt: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#5f6368", mb: 1, ml: 0.5, display: "block" }}>Reference Notes</Typography>

                                            {existingSampleBooklets.length > 0 && (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                                                    {existingSampleBooklets.map((sb, idx) => (
                                                        <Paper
                                                            key={idx}
                                                            variant="outlined"
                                                            sx={{
                                                                p: 1.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                borderRadius: "12px",
                                                                bgcolor: "#f0f7ff",
                                                                borderColor: "#cce5ff",
                                                                "&:hover": { bgcolor: "#e8f3ff" }
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 40, height: 40, borderRadius: "8px", bgcolor: "#fff",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                                                            }}>
                                                                <BookOpen size={20} color="#1a73e8" />
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#3c4043" }}>{sb.drive_file_name}</Typography>
                                                                <Typography variant="caption" sx={{ color: "#70757a" }}>{(sb.drive_file_size / 1024).toFixed(1)} KB · {sb.drive_file_extension?.toUpperCase()}</Typography>
                                                            </Box>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <Tooltip title="Preview">
                                                                    <IconButton size="small" onClick={(e) => handlePreviewWorksheet(e, sb)} sx={{ color: "#1a73e8" }}>
                                                                        <Eye size={18} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Remove">
                                                                    <IconButton size="small" onClick={() => setExistingSampleBooklets(prev => prev.filter((_, i) => i !== idx))} sx={{ color: "#d32f2f" }}>
                                                                        <CloseIcon sx={{ fontSize: 18 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </Paper>
                                                    ))}
                                                </Box>
                                            )}

                                            {refboardFiles.length > 0 && (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#1a73e8", mb: 0.5, ml: 0.5, display: "block" }}>Pending Reference Notes ({refboardFiles.length})</Typography>
                                                    {refboardFiles.map((file, idx) => (
                                                        <Paper
                                                            key={idx}
                                                            variant="outlined"
                                                            sx={{
                                                                p: 1.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                borderRadius: "12px",
                                                                bgcolor: "#e8f0fe",
                                                                borderColor: "#1a73e8",
                                                                borderStyle: "dashed"
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 40, height: 40, borderRadius: "8px", bgcolor: "#fff",
                                                                display: "flex", alignItems: "center", justifyContent: "center"
                                                            }}>
                                                                <FileText size={20} color="#1a73e8" />
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a73e8" }}>{file.name}</Typography>
                                                                <Typography variant="caption" sx={{ color: "#70757a" }}>{(file.size / 1024).toFixed(1)} KB</Typography>
                                                            </Box>
                                                            <IconButton size="small" onClick={() => setRefboardFiles(prev => prev.filter((_, i) => i !== idx))} sx={{ color: "#d32f2f" }}>
                                                                <CloseIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Paper>
                                                    ))}
                                                </Box>
                                            )}

                                            <Paper variant="outlined" sx={{
                                                p: 3,
                                                borderRadius: "16px",
                                                borderStyle: "dashed",
                                                borderColor: "#dadce0",
                                                bgcolor: "#f8f9fa",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                textAlign: "center",
                                                "&:hover": { bgcolor: "#f1f3f4", borderColor: "#1a73e8" },
                                                transition: "all 0.2s"
                                            }}>
                                                <input
                                                    type="file"
                                                    id="refboard-upload-modal"
                                                    multiple
                                                    accept=".pdf,.docx,.xls,.xlsx,.txt,.md"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const files = Array.from(e.target.files || []);
                                                        setRefboardFiles(prev => [...prev, ...files]);
                                                        e.target.value = "";
                                                    }}
                                                />
                                                <label htmlFor="refboard-upload-modal" style={{ width: "100%", cursor: "pointer" }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                                        <Box sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            bgcolor: "#e8f0fe",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}>
                                                            <CloudUploadIcon sx={{ fontSize: 20, color: "#1a73e8" }} />
                                                        </Box>
                                                        <Box sx={{ textAlign: "left" }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#202124" }}>
                                                                Upload Reference Notes
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "#5f6368" }}>
                                                                (Max 10MB each)
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </label>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </Box>




                                {/* Worksheet Materials Section */}
                                <Box sx={{ display: "flex", gap: 2.5 }}>
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "10px",
                                        bgcolor: "#e1f5fe",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}>
                                        <BookOpen size={20} color="#03a9f4" />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#3c4043", mb: 1.5 }}>
                                            Worksheet Materials
                                        </Typography>

                                        {existingWorksheets.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                    {existingWorksheets.map((ws, idx) => (
                                                        <Paper
                                                            key={idx}
                                                            variant="outlined"
                                                            sx={{
                                                                p: 1.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                borderRadius: "12px",
                                                                bgcolor: "#fbf6ff",
                                                                borderColor: "#e1d1f5",
                                                                "&:hover": { bgcolor: "#f5ebff" }
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 40, height: 40, borderRadius: "8px", bgcolor: "#fff",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                                                            }}>
                                                                <FileText size={20} color="#9c27b0" />
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#3c4043" }}>{ws.drive_file_name}</Typography>
                                                                <Typography variant="caption" sx={{ color: "#70757a" }}>{(ws.drive_file_size / 1024).toFixed(1)} KB · {ws.drive_file_extension?.toUpperCase()}</Typography>
                                                            </Box>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                <Tooltip title="Preview">
                                                                    <IconButton size="small" onClick={(e) => handlePreviewWorksheet(e, ws)} sx={{ color: "#9c27b0" }}>
                                                                        <Eye size={18} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Remove">
                                                                    <IconButton size="small" onClick={() => setExistingWorksheets(prev => prev.filter((_, i) => i !== idx))} sx={{ color: "#d32f2f" }}>
                                                                        <CloseIcon sx={{ fontSize: 18 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </Paper>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {worksheetFiles.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: "#1a73e8", mb: 1, display: "block" }}>
                                                    Pending Worksheets ({worksheetFiles.length})
                                                </Typography>
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                    {worksheetFiles.map((file, idx) => (
                                                        <Paper
                                                            key={idx}
                                                            variant="outlined"
                                                            sx={{
                                                                p: 1.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                borderRadius: "12px",
                                                                bgcolor: "#e8f0fe",
                                                                borderColor: "#1a73e8",
                                                                borderStyle: "dashed"
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 40, height: 40, borderRadius: "8px", bgcolor: "#fff",
                                                                display: "flex", alignItems: "center", justifyContent: "center"
                                                            }}>
                                                                <FileText size={20} color="#1a73e8" />
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a73e8" }}>{file.name}</Typography>
                                                                <Typography variant="caption" sx={{ color: "#70757a" }}>{(file.size / 1024).toFixed(1)} KB</Typography>
                                                            </Box>
                                                            <IconButton size="small" onClick={() => setWorksheetFiles(prev => prev.filter((_, i) => i !== idx))} sx={{ color: "#d32f2f" }}>
                                                                <CloseIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Paper>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {!isReadOnly && (
                                            <Paper variant="outlined" sx={{
                                                p: 2.5,
                                                borderRadius: "16px",
                                                borderStyle: "dashed",
                                                borderColor: "#dadce0",
                                                bgcolor: "#f8f9fa",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                textAlign: "center",
                                                "&:hover": { bgcolor: "#f1f3f4", borderColor: "#1a73e8" },
                                                transition: "all 0.2s"
                                            }}>
                                                <input
                                                    type="file"
                                                    id="worksheet-upload-modal"
                                                    multiple
                                                    accept=".pdf,.docx,.xls,.xlsx,.txt,.md"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => {
                                                        const files = Array.from(e.target.files || []);
                                                        setWorksheetFiles(prev => [...prev, ...files]);
                                                        e.target.value = "";
                                                    }}
                                                />
                                                <label htmlFor="worksheet-upload-modal" style={{ width: "100%", cursor: "pointer" }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                                        <Box sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            bgcolor: "#e8f0fe",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center"
                                                        }}>
                                                            <CloudUploadIcon sx={{ fontSize: 20, color: "#1a73e8" }} />
                                                        </Box>
                                                        <Box sx={{ textAlign: "left" }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#202124" }}>
                                                                Upload Worksheets
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "#5f6368" }}>
                                                                (Max 10MB each)
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </label>
                                            </Paper>
                                        )}
                                    </Box>
                                </Box>

                                                                {/* Description Section */}
                                <Box sx={{ display: "flex", gap: 2.5 }}>
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "10px",
                                        bgcolor: "#e8f5e9",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}>
                                        <FileText size={20} color="#4caf50" />
                                    </Box>
                                    <TextField
                                        fullWidth
                                        placeholder="Add class description or notes..."
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        value={description}
                                        disabled={isReadOnly}
                                        onChange={(e) => setDescription(e.target.value)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "16px",
                                                bgcolor: "#f8f9fa",
                                                "& fieldset": { borderColor: "rgba(0,0,0,0.08)" }
                                            }
                                        }}
                                    />
                                </Box>

                      {/* Google Meet Section */}
                                <Box sx={{
                                    p: 2.5,
                                    borderRadius: "20px",
                                    bgcolor: meetingLink ? "#f1f8ff" : "#f8f9fa",
                                    border: meetingLink ? "1px solid #cce5ff" : "1px dashed #dadce0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2
                                }}>
                                    <Box sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "12px",
                                        bgcolor: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                    }}>
                                        <img src={GoogleMeetIcon} alt="Google Meet" style={{ width: 43, height: 43, objectFit: 'contain' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        {meetingLink ? (
                                            <>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Live Class Room Added</Typography>
                                                <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>{meetingLink}</Typography>
                                                {!isReadOnly && (
                                                    <Typography variant="caption" sx={{ color: "#1a73e8", cursor: "pointer", fontWeight: 500 }} onClick={() => {
                                                        setMeetingLink("");
                                                    }}>Remove</Typography>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: formErrors.meetingLink ? "#d32f2f" : "inherit" }}>Video Conferencing <span style={{ color: '#d32f2f' }}>*</span></Typography>
                                                <Typography variant="caption" color={formErrors.meetingLink ? "error" : "textSecondary"}>
                                                    {formErrors.meetingLink ? formErrors.meetingLink : "Add a secure link for online classes"}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                    {!meetingLink && !isReadOnly && (
                                        <Button
                                            size="small"
                                            onClick={handleGenerateLink}
                                            variant="contained"
                                            disabled={isMeetLoading || !title.trim() || !startTime || !endTime}
                                            sx={{
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                bgcolor: "#1a73e8",
                                                px: 2,
                                                "&:hover": { bgcolor: "#1765cc" }
                                            }}
                                        >
                                            {isMeetLoading ? "Generating..." : "Add Link"}
                                        </Button>
                                    )}
                                    {meetingLink && (
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (meetingLink) {
                                                    setIsJoinConfirmOpen(true);
                                                }
                                            }}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                px: 2
                                            }}
                                        >
                                            Test Join
                                        </Button>
                                    )}
                                </Box>

                                


                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                                    <Button
                                        onClick={onClose}
                                        sx={{
                                            textTransform: "none",
                                            color: "#5f6368",
                                            fontWeight: 600,
                                            px: 4,
                                            borderRadius: "12px",
                                            fontSize:"14px"
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {!isReadOnly && (
                                        <Button
                                            variant="contained"
                                            onClick={handleSave}
                                            disabled={isCreateLoading}
                                            sx={{
                                                textTransform: "none",
                                                bgcolor: "#009dff",
                                                borderRadius: "12px",
                                                color:"#ffffff",
                                                px: 5,
                                                py: 1.2,
                                                fontSize:"14px",
                                                fontWeight: 600,
                                                boxShadow: "0 4px 12px rgba(26, 115, 232, 0.3)",
                                                "&:hover": { bgcolor: "#1765cc", boxShadow: "0 6px 16px rgba(26, 115, 232, 0.4)" }
                                            }}
                                        >
                                            {isCreateLoading ? "Saving..." : "Confirm Schedule"}
                                        </Button>
                                    )}
                                    {isReadOnly && (
                                        <Button
                                            variant="contained"
                                            onClick={onClose}
                                            sx={{
                                                textTransform: "none",
                                                bgcolor: "#5f6368",
                                                borderRadius: "12px",
                                                color:"#ffffff",
                                                px: 5,
                                                py: 1.2,
                                                fontSize:"14px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            Close
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </motion.div>
                        </DialogContent>
                    </Dialog>

            {/* Booklet Preview Dialog with Security Features */}
            <Dialog
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                fullScreen
                PaperProps={{
                    sx: {
                        bgcolor: "rgba(0,0,0,0.9)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden"
                    }
                }}
            >
                <Box sx={{
                    height: 60,
                    bgcolor: "#202124",
                    display: "flex",
                    alignItems: "center",
                    px: 3,
                    justifyContent: "space-between",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    zIndex: 2000
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#9c27b0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MenuBookIcon sx={{ color: "#fff", fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ color: "#fff", fontWeight: 600 }}>{previewTitle}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2, px: 1.5, py: 0.5, bgcolor: "rgba(255,255,255,0.1)", borderRadius: "20px" }}>
                            <VisibilityOffIcon sx={{ color: "#fb7185", fontSize: 14 }} />
                            <Typography sx={{ color: "#fb7185", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>Protected Preview</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 3, color: "rgba(255,255,255,0.5)" }}>
                            <PrintDisabledIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">Printing & Downloads Disabled</Typography>
                        </Box>
                        <IconButton onClick={() => setIsPreviewOpen(false)} sx={{ color: "#fff" }}>
                            <X size={24} />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent
                    sx={{
                        p: 0,
                        flex: 1,
                        display: "flex",
                        position: "relative",
                        "&::-webkit-scrollbar": { display: "none" }
                    }}
                    onContextMenu={(e) => e.preventDefault()} // Block right-click
                >
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        pointerEvents: "none",
                        zIndex: 100,
                        background: "rgba(255,255,255,0.01)",
                        "@media print": { display: "none" }
                    }} />

                    <iframe
                        src={`${previewUrl.replace("/view", "/preview")}`}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                        title="Booklet Preview"
                        onContextMenu={(e) => e.preventDefault()}
                    />

                    <GlobalStyles styles={{
                        "@media print": {
                            "body, iframe, #root, .MuiDialog-root": {
                                display: "none !important",
                            }
                        },
                        // Fix for the black background issue in MUI TimePicker selected state
                        ".MuiMultiSectionDigitalClock-root .Mui-selected": {
                            backgroundColor: "#1a73e8 !important",
                            color: "#fff !important"
                        },
                        ".MuiMultiSectionDigitalClockSection-item.Mui-selected": {
                            backgroundColor: "#1a73e8 !important",
                            color: "#fff !important"
                        }
                    }} />
                </DialogContent>
            </Dialog>

            {/* Test Join Confirmation Dialog */}
            <Dialog 
                open={isJoinConfirmOpen} 
                onClose={() => setIsJoinConfirmOpen(false)}
                PaperProps={{
                    sx: { borderRadius: "20px", width: "100%", maxWidth: "400px", p: 1 }
                }}
            >
                <DialogContent>
                    <Box sx={{ textAlign: "center", py: 2 }}>
                        <Box sx={{ 
                            width: 60, height: 60, borderRadius: "50%", bgcolor: "#e8f0fe", 
                            display: "flex", alignItems: "center", justifyContent: "center", 
                            mx: "auto", mb: 2 
                        }}>
                            <Box sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "12px",
                                        bgcolor: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                    }}>
                                        <img src={GoogleMeetIcon} alt="Google Meet" style={{ width: 43, height: 43, objectFit: 'contain' }} />
                                    </Box>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize:"14px" ,mb: 1, color: "#202124" }}>
                            Join Meeting?
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#5f6368",fontSize:"14px", mb: 4, px: 2 }}>
                            This will open the Google Meet classroom in a new browser tab.
                        </Typography>
                        
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => {
                                    window.open(meetingLink, '_blank');
                                    setIsJoinConfirmOpen(false);
                                }}
                                sx={{ 
                                    fontSize:"13px",
                                    color:"#ffffff",
                                    textTransform: "none", bgcolor: "#009dff", borderRadius: "12px", 
                                    py: 1.2, fontWeight: 600, boxShadow: "none", 
                                    "&:hover": { bgcolor: "#1765cc", boxShadow: "0 4px 12px rgba(26, 115, 232, 0.2)" } 
                                }}
                            >
                                Open in New Tab
                            </Button>
                            <Button
                                fullWidth
                                onClick={() => setIsJoinConfirmOpen(false)}
                                sx={{ 
                                    fontSize:"13px",
                                    textTransform: "none", color: "#5f6368", fontWeight: 600, 
                                    py: 1.2, "&:hover": { bgcolor: "#f1f3f4" } 
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MeetingModal;
